import React, { useState } from 'react';
import { Modal, Button, Table, Space, Input, Form, message, Popconfirm, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined, GlobalOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import MultilingualDialog from '../../AppService/AppPackage/components/MultilingualDialog';

const { Text } = Typography;

/**
 * 商城位运营 - 分类管理弹窗（支持拖拽排序 / 编码 / 多语言）
 * 真实项目：views/operation/mall/components/CategoryManageDialog.vue
 */
const MallCategoryManageDialog = ({ open, categories, onCancel, onChange }) => {
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [i18n, setI18n] = useState({ open: false, originValue: '' });
    const [form] = Form.useForm();

    const openAdd = () => {
        setEditingId(null);
        form.resetFields();
        setFormOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id);
        form.setFieldsValue({ name: row.name, code: row.code });
        setFormOpen(true);
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        const duplicate = categories.some(c => c.code === values.code && c.id !== editingId);
        if (duplicate) { message.error(`分类编码「${values.code}」已存在，请使用其他编码`); return; }
        if (editingId) {
            onChange(categories.map(c => (c.id === editingId ? { ...c, ...values } : c)));
            message.success('编辑成功');
        } else {
            onChange([...categories, { id: Date.now(), ...values, packageCount: 0 }]);
            message.success('添加成功');
        }
        setFormOpen(false);
    };

    const handleDelete = (row) => {
        onChange(categories.filter(c => c.id !== row.id));
        message.success('删除成功');
    };

    const handleDrop = (index) => {
        if (dragIndex === null || dragIndex === index) { setDragIndex(null); return; }
        const list = [...categories];
        const [moved] = list.splice(dragIndex, 1);
        list.splice(index, 0, moved);
        onChange(list);
        setDragIndex(null);
    };

    const columns = [
        {
            title: '分类名称', key: 'name', width: 240,
            render: (_, record, index) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <HolderOutlined
                        draggable
                        onDragStart={() => setDragIndex(index)}
                        onDragEnd={() => setDragIndex(null)}
                        style={{ color: '#909399', cursor: 'move' }}
                    />
                    <span>{record.name}</span>
                    <GlobalOutlined
                        style={{ color: '#909399', cursor: 'pointer', marginLeft: 4 }}
                        onClick={() => setI18n({ open: true, originValue: record.name })}
                    />
                </div>
            ),
        },
        { title: '分类编码', key: 'code', width: 160, render: (_, r) => <Text code style={{ fontSize: 12 }}>{r.code || '—'}</Text> },
        { title: '套餐数量', key: 'packageCount', width: 120, align: 'center', render: (_, r) => r.packageCount ?? 0 },
        {
            title: '操作', key: 'action', width: 150, align: 'center',
            render: (_, record) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
                    <Popconfirm title={`该操作将影响APP商城页的展示效果，确定删除分类【${record.name}】？`} okText="确定" cancelText="取消" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(record)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Modal title="分类管理" open={open} onCancel={onCancel} width={700} destroyOnClose footer={<Button icon={<CloseOutlined />} onClick={onCancel}>关闭</Button>}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 14, color: '#909399' }}>拖拽可调整排序</span>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加分类</Button>
            </div>

            <Table
                dataSource={categories}
                rowKey="id"
                columns={columns}
                pagination={false}
                size="small"
                onRow={(record, index) => ({
                    onDragOver: e => e.preventDefault(),
                    onDrop: () => handleDrop(index),
                })}
            />

            <Modal
                title={editingId ? '编辑分类' : '添加分类'}
                open={formOpen}
                onCancel={() => setFormOpen(false)}
                width={500}
                destroyOnClose
                footer={
                    <Space>
                        <Button icon={<CloseOutlined />} onClick={() => setFormOpen(false)}>取消</Button>
                        <Button type="primary" icon={<CheckOutlined />} onClick={handleSave}>确定</Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="分类名称" name="name" rules={[{ required: true, message: '请输入分类名称' }, { max: 20, message: '分类名称长度在 1 到 20 个字符' }]}>
                        <Input placeholder="请输入分类名称" maxLength={20} showCount />
                    </Form.Item>
                    <Form.Item label="分类编码" name="code" rules={[{ required: true, message: '请输入分类编码' }]}>
                        <Input placeholder="请输入分类编码（如 cloud_storage）" maxLength={64} showCount />
                    </Form.Item>
                </Form>
            </Modal>

            <MultilingualDialog
                open={i18n.open}
                title="分类名称—多语言翻译"
                originValue={i18n.originValue}
                onCancel={() => setI18n({ open: false, originValue: '' })}
            />
        </Modal>
    );
};

export default MallCategoryManageDialog;
